import { getRemoteConfigValues } from '@/lib/remote-config/client';

import type {
  ReviewFlowConfig,
  ReviewFlowTriggerConfig,
  ReviewFlowTriggerKey,
} from '../types';

export const DEFAULT_REVIEW_FLOW_CONFIG: ReviewFlowConfig = {
  enabled: true,
  triggers: {
    download_success: { enabled: true, trigger_at: [1, 5] },
    editor_edit: { enabled: true, trigger_at: [5, 15] },
  },
};

const REMOTE_CONFIG_DEFAULTS = {
  reviewFlowConfig: JSON.stringify(DEFAULT_REVIEW_FLOW_CONFIG),
} as const;

const TRIGGER_KEYS: readonly ReviewFlowTriggerKey[] = [
  'download_success',
  'editor_edit',
];

const isPositiveIntegerArray = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.every(
    (item) => typeof item === 'number' && Number.isInteger(item) && item > 0
  );

const isTriggerConfig = (value: unknown): value is ReviewFlowTriggerConfig =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { enabled?: unknown }).enabled === 'boolean' &&
  isPositiveIntegerArray((value as { trigger_at?: unknown }).trigger_at);

const isReviewFlowConfig = (value: unknown): value is ReviewFlowConfig => {
  if (typeof value !== 'object' || value === null) return false;

  const candidate = value as { enabled?: unknown; triggers?: unknown };

  if (typeof candidate.enabled !== 'boolean') return false;
  if (typeof candidate.triggers !== 'object' || candidate.triggers === null) {
    return false;
  }

  const triggers = candidate.triggers as Record<string, unknown>;

  return TRIGGER_KEYS.every((key) => isTriggerConfig(triggers[key]));
};

export const parseReviewFlowConfig = (
  value: string | null | undefined
): ReviewFlowConfig => {
  if (!value) {
    return DEFAULT_REVIEW_FLOW_CONFIG;
  }

  try {
    const config: unknown = JSON.parse(value);

    if (isReviewFlowConfig(config)) {
      return config;
    }
  } catch {
    return DEFAULT_REVIEW_FLOW_CONFIG;
  }

  return DEFAULT_REVIEW_FLOW_CONFIG;
};

export const getReviewFlowConfig = async (): Promise<ReviewFlowConfig> => {
  const { reviewFlowConfig } = await getRemoteConfigValues(
    REMOTE_CONFIG_DEFAULTS
  );

  return parseReviewFlowConfig(reviewFlowConfig);
};
